/** @jsxImportSource @emotion/react */
import { CardHeader } from "../../../components/CardHeader";
import { FormRow } from "../../common/hook-form";
import { useFormContext } from "react-hook-form";
import * as Inputs from "../../common/hook-form/index";
import { config } from "../../../config";
import { PriceInput } from "../../../components/forms/inputs/PriceInput";
import {
  ChallengeFormType,
  ObjectiveType,
} from "../../../service/api-response/Challenges";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { Country } from "../../../model/Country";
import { SelectProductInput } from "../../../components/forms/inputs/SelectProductInput";

type Props = {
  countries?: Country[];
};

export const ChallengeObjectiveSection = ({ countries }: Props) => {
  const { register, formState, watch, clearErrors } = useFormContext();
  const objectiveType = watch("objectiveType");
  const methods = useFormContext<ChallengeFormType>();
  const currency = watch("currency");
  // If staging or local, show order created option
  const showOrderCreated =
    config.environment === "staging" || !config.environment;

  const selectCurrencyOptions = new Set(
    countries?.filter((c) => c.enabled).map((c) => c.currency)
  );

  const orderCreatedOption = [
    {
      label: "Order created",
      value: ObjectiveType.ORDER_CREATED,
    },
  ];

  const objectiveTypeOptions = [
    {
      label: "Money spent",
      value: ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD,
    },
    {
      label: "First time order",
      value: ObjectiveType.ORDER_DELIVERED_FIRST_TIME,
    },
    {
      label: "Number of orders placed",
      value: ObjectiveType.ORDER_DELIVERED_ORDER_COUNT,
    },
    {
      label: "Number of purchases of a specific product",
      value: ObjectiveType.ORDER_DELIVERED_PRODUCT_COUNT,
    },
    ...(showOrderCreated ? orderCreatedOption : []),
  ];

  const returnObjectiveTypeHelptest = () => {
    switch (objectiveType) {
      case ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD:
        return `Money spent: A user will have to spend XX amount of ${currency} within validity dates to earn reward.`;
      case ObjectiveType.ORDER_CREATED:
        return "User has to make X number of orders to earn reward.";
      case ObjectiveType.ORDER_DELIVERED_FIRST_TIME:
        return "Reward a user for “First purchase”. The Reward will be earned after the first completed order from a restaurant. Orders of value 0 and digital purchases (punch cards, voucher offers etc. will not count). Enter minimum amount of the order to be valid below. Users that have already made an order prior activating this Reward will not see or be able to get this reward, meaning only users that has not made a purchase can participate";
      case ObjectiveType.ORDER_DELIVERED_ORDER_COUNT:
        return "User has to make X number of orders to earn reward.";
      case ObjectiveType.ORDER_DELIVERED_PRODUCT_COUNT:
        return "User has to make X number of purchases of a specific product to earn reward.";
      default:
        return "Choose challenge objective";
    }
  };

  const returnGoalHelpText = () => {
    switch (objectiveType) {
      case ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD:
        return `Enter the amount in ${currency} the user need to spend to complete this challenge`;
      case ObjectiveType.ORDER_DELIVERED_FIRST_TIME:
        return `Enter minimum value of ${currency} for order to be counted as “First purchase”. Minimum 1 ${currency}.`;
      case ObjectiveType.ORDER_DELIVERED_ORDER_COUNT:
        return "Enter the number of orders the user needs to place to earn reward.";
      case ObjectiveType.ORDER_DELIVERED_PRODUCT_COUNT:
        return "Enter the number of purchases of a specific product the user needs to make to earn reward.";
      default:
        return "Choose challenge goal";
    }
  };

  const returnCurrencyHelperText = () => {
    switch (objectiveType) {
      case ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD:
        return "Select the currency the objective goal is in";
      case ObjectiveType.ORDER_DELIVERED_FIRST_TIME:
        return "Select the currency for the minimum order value";
      default:
        return "Choose challenge currency";
    }
  };

  return (
    <div className="card">
      <CardHeader
        title="Challenge objectives"
        subtitle="Select how the reward is earned by the user."
      />
      <div className="card-body">
        <Inputs.SelectFormField
          errors={formState.errors}
          register={register}
          label="Challenge objective type"
          name="objectiveType"
          helpText={returnObjectiveTypeHelptest()}
          options={objectiveTypeOptions}
          clearErrors={clearErrors}
        />

        <FormRow>
          {objectiveType === ObjectiveType.ORDER_DELIVERED_NO_GIFT_CARD ||
          objectiveType === ObjectiveType.ORDER_DELIVERED_FIRST_TIME ? (
            <PriceInput
              formMethods={methods}
              name="objectiveGoal"
              label={`Objective goal (${currency})`}
              currencyString={currency}
              helpertext={returnGoalHelpText()}
            />
          ) : (
            <Inputs.InputFormField
              errors={formState.errors}
              register={register}
              label="Goal"
              name="objectiveGoal"
              helpText={returnGoalHelpText()}
            />
          )}
          <SelectInput
            name="currency"
            label="Currency"
            helperText={returnCurrencyHelperText()}
            options={[...selectCurrencyOptions].map((c) => ({
              label: c,
              value: c,
            }))}
          />
        </FormRow>
        {objectiveType === ObjectiveType.ORDER_DELIVERED_PRODUCT_COUNT && (
          <FormRow className="mt-4">
            <SelectProductInput
              isMulti
              closeMenuOnSelect={false}
              name="objectiveProductIds"
              label="Products to be purchased"
              helperText="Purchasing any of these products will earn progress for this challenge."
              classification="admin-main-list"
            />
          </FormRow>
        )}
      </div>
    </div>
  );
};
